import styled, { css, type DefaultTheme } from 'styled-components';

import { darken, transparentize } from 'polished';
import { hoverColor, isAdjustableColor, parseColor, parseDimension, setAlpha } from '../../../styles';
import {
  FADED_CELL_CLASSNAME,
  FADED_ROW_CLASSNAME,
  LIGHTEN_CELL_CLASSNAME,
  NEGATIVE_CELL_CLASSNAME,
  NEGATIVE_CELL_DARK_CLASSNAME,
  NEGATIVE_CELL_DIM_CLASSNAME,
  NEGATIVE_CELL_LIGHTEN_CLASSNAME,
  NEGATIVE_ROW_CLASSNAME,
  POSITIVE_CELL_CLASSNAME,
  POSITIVE_CELL_DARK_CLASSNAME,
  POSITIVE_CELL_DIM_CLASSNAME,
  POSITIVE_CELL_LIGHTEN_CLASSNAME,
  POSITIVE_ROW_CLASSNAME,
  PRIMARY_ROW_CLASSNAME,
  SUBTLE_TEXT_CELL_CLASSNAME,
  WARNING_CELL_MED_CLASSNAME,
  WARNING_ROW_CLASSNAME,
} from '../../AgGrid/types';
import { Button } from '../../Button';
import { Box, VStack } from '../../Core';
import { FormRowStatus } from '../../FormTable/types';
import { NumberWrapper } from '../../FormattedNumber';
import { BlotterDensity } from '../types';
import { agGridCheckboxStyles } from './agGridCheckboxStyles';
import { blotterPopupStyles } from './blotterPopupStyles';

const getFontSize = (theme: DefaultTheme, density?: BlotterDensity) =>
  density === BlotterDensity.Compact ? theme.fontSizeBlotterTableCompact : theme.fontSizeBlotterTableDefault;

/** Empty and filtered-empty use separate AG Grid class pairs. Keep one text treatment for both. */
export const agOverlayEmptyCenterText = (theme: DefaultTheme) => css`
  .ag-overlay-no-rows-center,
  .ag-overlay-no-matching-rows-center {
    color: ${theme.colorTextMuted};
    font-weight: ${theme.fontWeightRegular};
    font-size: ${theme.fontSizeBig}rem;
  }
`;

const addedFormRowSelector = `.ag-row.${FormRowStatus.Added}, .ag-row.${FormRowStatus.Added}:hover`;
const updatedFormRowSelector = `.ag-row.${FormRowStatus.Updated}, .ag-row.${FormRowStatus.Updated}:hover`;
const removedFormRowSelector = `.ag-row.${FormRowStatus.Removed}, .ag-row.${FormRowStatus.Removed}:hover`;

/**
 * Given the density of the blotter, will return the x-axis padding applied to headers and cells. For cells, the value is
 * subtracted by 1 to account for the 1px border that is present.
 */
export const getBlotterHeaderAndCellPadding = (theme: DefaultTheme, density?: BlotterDensity) =>
  density === BlotterDensity.Compact
    ? theme.spacing4
    : density === BlotterDensity.Comfortable
      ? theme.spacing16
      : theme.spacing8;

export const blotterStyles = (
  theme: DefaultTheme,
  density?: BlotterDensity,
  background?: string,
  rowEndSpacing?: string | number,
  headerBackground?: string,
  hideHeaderBorder?: boolean,
  hideColumnDivider?: boolean
) => {
  const parsedColor = background && background !== 'transparent' ? parseColor(theme, background) : undefined;
  const parsedHeaderBackground =
    headerBackground && headerBackground !== 'transparent' ? parseColor(theme, headerBackground) : headerBackground;
  const backgroundColor = parsedColor ? parseColor(theme, parsedColor) : theme.backgroundBlotterTableRow;
  const hoverBackgroundColor = parsedColor ? hoverColor(theme, parsedColor) : theme.backgroundBlotterTableRowHover;
  const selectedBackgroundColor = parsedColor
    ? hoverColor(theme, parsedColor, 0.1)
    : theme.backgroundBlotterTableRowSelected;
  const borderColor = parsedColor ? darken(0.05, parsedColor) : theme.borderColorBlotterTable;

  const { columnDividerColor, columnDividerCss } = calculateDividerStyles(theme, hideColumnDivider);

  return css`
    font-family: ${theme.fontFamily};
    font-size: ${getFontSize(theme, density)}rem;
    height: 100%; // Combats Sticky Headers not working, by allowing us to remove domLayout="autoHeight"

    // Root
    --ag-size: 3px; // Not working?
    --ag-border-radius: 0px;
    --ag-foreground-color: ${theme.colorTextImportant};
    --ag-data-color: ${theme.colorTextImportant};
    --ag-secondary-foreground-color: ${theme.colorTextSubtle};
    --ag-disabled-foreground-color: ${theme.colorTextMuted};
    --ag-background-color: ${backgroundColor};
    --ag-font-size: ${getFontSize(theme, density)}rem;
    --ag-header-column-separator-color: ${columnDividerColor};
    --hoverBackgroundColor: ${hoverBackgroundColor};
    --ag-selected-row-background-color: ${selectedBackgroundColor};

    // Row group indentation
    --ag-row-group-indent-size: calc((var(--spacing16) + var(--spacing6)) * 1px);
    --ag-cell-widget-spacing: calc(var(--spacing6) * 1px);

    // Row group icons
    --ag-icon-font-family-tree-open: 'helixicons';
    --ag-icon-font-family-tree-closed: 'helixicons';
    --ag-icon-font-code-tree-open: '\\e92d';
    --ag-icon-font-code-tree-closed: '\\e92f';
    --ag-icon-font-color-tree-closed: rgb(from var(--colorTextImportant) r g b / 56%);
    --ag-icon-font-color-tree-open: rgb(from var(--colorTextImportant) r g b / 56%);
    .ag-group-contracted,
    .ag-group-expanded {
      &:hover,
      &:active,
      &:focus {
        --ag-icon-font-color-tree-closed: var(--colorTextImportant);
        --ag-icon-font-color-tree-open: var(--colorTextImportant);
      }
    }

    // Hide border around grid
    .ag-root-wrapper {
      border: 0;
    }

    // No-rows overlay: keep the shell transparent so column headers stay visible; keep pointer-events off the
    // full-bleed wrapper so clicks reach the headers; re-enable them on the overlay root direct child only.
    // Deeper nodes keep the initial pointer-events value (auto); no universal descendant selector needed.
    .ag-overlay {
      background: transparent;
    }

    .ag-overlay-loading-wrapper {
      background: var(--ag-background-color);
    }

    .ag-overlay-no-rows-wrapper {
      background: transparent;
      pointer-events: none;
    }

    .ag-overlay-no-rows-wrapper > * {
      pointer-events: auto;
    }

    ${agOverlayEmptyCenterText(theme)}

    .ag-tooltip-custom {
      position: absolute;
    }

    .ag-tooltip {
      max-width: 25rem;
    }

    // Header
    --ag-header-foreground-color: ${theme.colorBlotterTableHeader};
    --ag-header-background-color: ${parsedHeaderBackground ?? backgroundColor};
    --ag-border-color: ${borderColor};

    ${hideHeaderBorder
      ? `
      .ag-header {
        border-bottom: none;
      }
    `
      : ''}
    --ag-subheader-background-color: transparent;
    --ag-subheader-toolbar-background-color: transparent;
    --ag-header-cell-moving-background-color: ${hoverBackgroundColor};

    .ag-header-cell.hide-header-label {
      .ag-header-cell-text {
        visibility: hidden;
      }
    }

    .ag-header-cell,
    .ag-header-group-cell,
    .ag-header-cell-group {
      font-size: ${theme.fontSizeBlotterTableHeader}rem;
      text-transform: ${theme.blotterTableHeaderTextTransform};
      font-weight: ${({ theme }) => theme.blotterTableHeaderFontWeight};
      padding: 0 ${getBlotterHeaderAndCellPadding(theme, density)}px;

      &.ag-header-cell-sortable:hover {
        color: ${({ theme }) => theme.colorTextImportant};
      }
      .ag-header-cell-menu-button {
        cursor: pointer;
        color: ${({ theme }) => theme.colorTextImportant};
      }
    }

    ${rowEndSpacing &&
    css`
      .ag-header-cell,
      .ag-cell {
        &.ag-column-first {
          padding-left: ${parseDimension(theme, rowEndSpacing)};
        }
        &.ag-column-last {
          padding-right: ${parseDimension(theme, rowEndSpacing)};
        }
      }
    `}

    .ag-header-cell-sorted-desc,
    .ag-header-cell-sorted-asc {
      color: ${({ theme }) => theme.colorTextImportant};
    }

    // For columns that are not sortable, hide the sort arrow since they render anyway (bug in ag-grid)
    .ag-custom-column-group:not(.ag-header-cell-sortable) {
      .hi-arrow-up,
      .hi-arrow-down {
        visibility: hidden;
      }
    }

    .ag-header-cell.no-handle:after {
      display: none;
    }

    // Show column divider on hover, before and after
    ${columnDividerCss}

    .ag-header-cell {
      .ag-header-cell-menu-button {
        flex: 0 1 1px;
        overflow: hidden;
        transition:
          flex 200ms ease 500ms,
          opacity 200ms ease 500ms;
        opacity: 0;
      }

      &.ag-header-active,
      &.ag-column-menu-visible {
        .ag-header-cell-menu-button {
          flex: 0 0 16px;
          opacity: 1;
          transition:
            flex 200ms ease,
            opacity 200ms ease;
        }
      }
    }

    .ag-header-group-cell {
      .ag-header-group-text:has(.ag-header-group-inner-header-root) {
        width: 100%;
      }

      &.ag-header-active,
      &.ag-column-menu-visible {
        .ag-header-group-menu-button,
        .ag-header-group-menu-button.ag-header-group-menu-button-visible {
          flex-basis: var(--ag-icon-size, 16px);
          opacity: 1;
        }
      }
    }

    .ag-keyboard-focus {
      .ag-header-cell {
        &:hover:after,
        &:hover:before {
          background-color: transparent;
        }
      }
    }

    .ag-header-no-title {
      .ag-header-cell-comp-wrapper {
        height: 100%;
        display: flex;
        justify-content: center;
      }
      .ag-header-cell-text {
        display: none;
      }
      padding: 0;
      .ag-header-cell-label .ag-sort-indicator-icon {
        margin-left: 0;
        padding-left: 0;
      }
    }

    // This can be referenced by ColDef.headerClass.
    // Used for when you want to render a custom talos <Checkbox> component yourself in an aggrid header cell.
    .ag-header-custom-checkbox {
      .ag-header-cell-text {
        display: none;
      }
      padding: 0;
    }

    .ag-column-drop-horizontal {
      background-color: inherit;
    }

    .ag-column-drop-cell {
      background: ${theme.backgroundDefaultButton};
      height: 20px;
      color: ${theme.colorTextDefaultButton};
    }

    // Rows
    --ag-row-border-color: ${borderColor};
    --ag-row-hover-color: ${hoverBackgroundColor};

    --ag-odd-row-background-color: ${backgroundColor
      ? isAdjustableColor(backgroundColor)
        ? setAlpha(0.5, backgroundColor)
        : backgroundColor
      : theme.backgroundBlotterTableRow};
    --ag-value-change-value-highlight-background-color: ${theme.backgroundPrimarySubtle};
    .ag-row {
      transition: background 250ms;

      &.ag-row-selected:has(.ag-column-first)::after {
        content: '';
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: ${theme.colors.primary.default};
      }

      &.ag-row-hover.ag-row-selected::before {
        background-image: none;
      }
    }

    // Imitate a filler column at the end to cover the entire width
    .ag-center-cols-container {
      min-width: 100%;
    }

    ${addedFormRowSelector} {
      background: ${theme.backgroundPositiveMuted};
      font-style: italic;
      opacity: 1;
    }

    ${updatedFormRowSelector} {
      background: ${theme.backgroundPrimaryMuted};
      font-style: italic;
      opacity: 1;
    }

    ${removedFormRowSelector} {
      background: ${theme.backgroundNegativeMuted};
      font-style: italic;
      opacity: 1;
    }

    // Widgets
    --ag-modal-overlay-background-color: ${(typeof background === 'string'
      ? parseColor(theme, background)
      : background) ?? theme.backgroundContent};

    // Groups
    .ag-row-group {
      align-items: center;
    }

    .ag-group-child-count {
      color: ${theme.colorTextSubtle};
      font-size: ${theme.fontSizeBlotterTableDefault}rem;
      margin-left: ${theme.spacing4}px;
    }

    .ag-row-level-1,
    .ag-row-level-2,
    .ag-row-level-3,
    .ag-row-level-4,
    .ag-row-level-5 {
      .ag-row-group-indent-1,
      .ag-row-group-indent-2,
      .ag-row-group-indent-3,
      .ag-row-group-indent-4,
      .ag-row-group-indent-5 {
        &.ag-row-group-leaf-indent {
          margin-left: 0;
        }
        &:not(.ag-cell-expandable) {
          &::before {
            font-family: helixicons;
            font-size: var(--ag-icon-size);
            line-height: var(--ag-icon-size);
            content: '\\ea08';
            color: rgb(from var(--colorTextDefault) r g b / 32%);
            display: inline-block;
            padding-right: calc(var(--spacing6) * 1px);
          }
        }
      }
    }

    .ag-cell,
    .ag-header-cell {
      &.ag-center-aligned-cell .ag-header-cell-label,
      &.ag-center-aligned-cell,
      &.ag-center-aligned-cell .ag-react-container {
        justify-content: center;
      }
      &.ag-right-aligned-cell .ag-header-cell-label,
      &.ag-right-aligned-cell,
      &.ag-right-aligned-cell .ag-react-container {
        justify-content: flex-end;
      }
      &.ag-custom-column-group.ag-custom-column-group {
        background: ${({ theme }) => theme.colors.gray['030']};
        &:hover {
          background: ${({ theme }) => theme.colors.gray['040']};
        }
      }
    }

    --ag-range-selection-border-color: transparent;

    .ag-cell {
      // - 1 to take invisible border into account
      padding: 0 ${getBlotterHeaderAndCellPadding(theme, density) - 1}px;
      line-height: 1;

      overflow: hidden;
      display: flex;
      align-items: center;

      // For number cells that are aligned right
      &.ag-right-aligned-cell {
        &:has(${NumberWrapper}) {
          &:hover {
            overflow: visible;
            ${NumberWrapper} {
              // show the full number on hover, with a background color as well as some spacing to the left
              // to ensure that (any possible) number to the left bleed into this cell.
              padding-left: ${theme.spacing2}px;
              background-color: var(--hoverBackgroundColor);
            }
          }
        }
      }

      // Remove left side border of right pinned cells
      &.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell) {
        border-left: transparent;
      }

      // Remove right side border of left pinned cells
      &.ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell) {
        border-right: transparent;
      }

      // Increase left padding for first cell in a row
      .ag-cell-value[aria-colindex='1'],
      .ag-header-cell[aria-colindex='1'] {
        padding-left: ${({ theme }) => theme.spacing16}px;
      }

      &.editable {
        transition: background 150ms;

        &:hover {
          background: ${theme.colors.gray['030']};
          cursor: pointer;
        }
      }

      &.invalid {
        border-color: ${({ theme }) => theme.colors.red.lighten};
      }

      // Allow cell values to overflow when hovering to support showing all decimals of a number
      .ag-cell-value:hover {
        overflow: visible;
        z-index: 1;
      }

      // Allow InlineFormattedNumber to inherit background when hovering
      .${NumberWrapper} {
        background: inherit;
      }
    }

    // Checkbox styling
    ${agGridCheckboxStyles(theme)}

    // Gradient for pinned column to indicate more content
    .ag-pinned-right-cols-container {
      box-shadow:
        0 0 15px ${({ theme }) => backgroundColor},
        0 0 3px ${({ theme }) => backgroundColor};
    }
    .ag-pinned-right-header {
      box-shadow:
        0 0 15px ${({ theme }) => backgroundColor},
        0 0 3px ${({ theme }) => backgroundColor};
    }

    // Inputs
    .ag-react-container:not(.ag-tooltip-custom) {
      display: flex;
      align-items: center;
      height: 100%;
      width: 100%;
    }

    .${WARNING_ROW_CLASSNAME} {
      background-color: ${({ theme }) => transparentize(1 - 0.12, theme.colors.red.default)};
      --ag-row-hover-color: ${({ theme }) => transparentize(1 - 0.24, theme.colors.red.default)};
      --ag-selected-row-background-color: ${({ theme }) => transparentize(1 - 0.36, theme.colors.red.default)};
    }

    .${PRIMARY_ROW_CLASSNAME} {
      background-color: ${({ theme }) => transparentize(1 - 0.12, theme.colors.primary.default)};
      --ag-row-hover-color: ${({ theme }) => transparentize(1 - 0.24, theme.colors.primary.default)};
      --ag-selected-row-background-color: ${({ theme }) => transparentize(1 - 0.36, theme.colors.primary.default)};
    }

    .${FADED_ROW_CLASSNAME} {
      opacity: 0.5;
    }

    .${POSITIVE_ROW_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.56, theme.colors.green.default)};
      --ag-row-hover-color: ${({ theme }) => setAlpha(0.56, theme.colors.green.default)};
      --ag-selected-row-background-color: ${({ theme }) => setAlpha(0.56, theme.colors.green.default)};
    }

    .${NEGATIVE_ROW_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.56, theme.colors.red.default)};
      --ag-row-hover-color: ${({ theme }) => setAlpha(0.56, theme.colors.red.default)};
      --ag-selected-row-background-color: ${({ theme }) => setAlpha(0.56, theme.colors.red.default)};
    }

    .${WARNING_CELL_MED_CLASSNAME} {
      background-color: ${({ theme }) => transparentize(1 - 0.2, theme.colors.yellow.default)};
    }

    .${POSITIVE_CELL_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.56, theme.colors.green.default)};
    }

    .${POSITIVE_CELL_LIGHTEN_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.55, theme.colors.green.default)};
    }

    .${POSITIVE_CELL_DIM_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.16, theme.colors.green.default)};
    }

    .${POSITIVE_CELL_DARK_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.02, theme.colors.green.default)};
    }

    .${NEGATIVE_CELL_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.56, theme.colors.red.default)};
    }

    .${NEGATIVE_CELL_LIGHTEN_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.55, theme.colors.red.default)};
    }

    .${NEGATIVE_CELL_DIM_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.16, theme.colors.red.default)};
    }

    .${NEGATIVE_CELL_DARK_CLASSNAME} {
      background-color: ${({ theme }) => setAlpha(0.02, theme.colors.red.default)};
    }

    .${FADED_CELL_CLASSNAME} {
      opacity: 0.5;
    }

    .${LIGHTEN_CELL_CLASSNAME} {
      background-image: linear-gradient(rgb(255 255 255 / 4%), rgb(255 255 255 / 4%));
    }

    .${SUBTLE_TEXT_CELL_CLASSNAME} {
      color: ${({ theme }) => theme.colorTextSubtle};
    }

    .ag-cell-warning {
      padding-left: 0;
      padding-right: 0;
      > .ag-react-container {
        align-items: stretch;
      }
    }

    .ag-cell-inline-editing {
      border: none;
      background: transparent;
      box-shadow: none;
      height: auto;
      padding: 0;
    }

    // Context menu
    .ag-menu {
      background: ${({ theme }) => theme.backgroundPopover};
    }

    .ag-menu-option-active {
      background-color: ${({ theme }) => darken(0.03)(theme.backgroundPopover)};
    }

    .ag-rich-select .ag-rich-select-list {
      width: auto !important;
    }

    .ag-aria-description-container {
      /* Remove helpful aria text above the blotter */
      display: none;
    }

    .ag-checkbox-input {
      cursor: pointer;
    }
  `;
};

export const BlotterTableWrapper = styled(Box)<{
  density?: BlotterDensity;
  background?: string;
  headerBackground?: string;
  hideHeaderBorder?: boolean;
  pinnedLeftOverlapping?: boolean;
  pinnedRightBorderHidden?: boolean;
  hidden?: boolean;
  rowEndSpacing?: string | number;
  hideColumnDivider?: boolean;
}>`
  height: 100%;
  width: 100%;

  p {
    color: ${({ theme }) => theme.colorTextAttention};
    margin: 0;
    padding: 0;
  }

  ${({ hidden }) =>
    hidden &&
    css`
      display: none;
    `}

  &.ag-theme-balham-dark {
    // See https://www.ag-grid.com/javascript-grid/themes-customising/#full-list-of-theme-parameters

    ${({ theme, density, background, rowEndSpacing, headerBackground, hideHeaderBorder, hideColumnDivider }) =>
      blotterStyles(theme, density, background, rowEndSpacing, headerBackground, hideHeaderBorder, hideColumnDivider)}

    // Avoid showing the default ag-grid pinned border (unless scrolled)
    .ag-pinned-left-header {
      border-right: transparent;
    }

    /** By default we always show a border-left on right-pinned grid sections
    * - for cases like Watchlist, which has a hover-triggered button display on the right
    * pinned section, we want to hide this border.
    */
    ${({ pinnedRightBorderHidden }) =>
      !pinnedRightBorderHidden &&
      css`
        .ag-pinned-right-cols-container,
        .ag-pinned-right-sticky-top,
        .ag-pinned-right-header {
          border-left: 1px solid var(--ag-border-color);
        }
      `}

    // Only show the left-pinned border when scrolled
    // - ideally the right-pinned border should be shown as well, but that is tough with the current ag-grid API
    // and not part of the current requirements.
    // https://stackoverflow.com/q/73910201/11076115
    ${({ pinnedLeftOverlapping, theme }) =>
      pinnedLeftOverlapping &&
      css`
        .ag-pinned-left-cols-container,
        .ag-pinned-left-sticky-top,
        .ag-pinned-left-header {
          border-right: 1px solid var(--ag-border-color);
        }
      `}
  }

  // We separately override the ag-popup related styles too.
  .ag-theme-balham-dark.ag-popup {
    ${({ theme, background }) => blotterPopupStyles(theme, background)}
  }
`;

export const CustomerRolePillWrapper = styled(Box)`
  background: ${({ theme }) => theme.colors.gray['030']};
  padding: ${({ theme }) => `${theme.spacing4}px ${theme.spacing8}px`};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  white-space: nowrap;
`;

export const BlotterMenuWrapper = styled(VStack)`
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing8}px;

  ${Button} {
    justify-content: flex-start;
    width: 100%;
  }
`;

/**
 * Calculate the divider styles for the Grid column dividers
 */
function calculateDividerStyles(
  theme: DefaultTheme,
  hideColumnDivider?: boolean
): {
  /** The color of the divider (used in the base Ag-grid styles) */
  columnDividerColor: string;
  /** The css for the column divider hover behavior */
  columnDividerCss: ReturnType<typeof css> | string;
} {
  const singleHeaderDividerPercent = 50;
  const doubleHeaderDividerPercent = 35;
  const columnDividerCss = css`
    .ag-header-cell,
    .ag-header-group-cell-with-group {
      // overflow: visible and :before left: -1px allows the left
      // divider to visually overlap the previous column
      // set before bgColor to transparent so we don't get doubled alpha transparency
      overflow: visible;
      &:before {
        left: -1px;
        background-color: transparent;
      }
      &:after {
        right: 0;
        background-color: ${hideColumnDivider ? 'transparent' : theme.columnDividerDefault};
      }
      &:before,
      &:after {
        content: '';
        position: absolute;
        z-index: 1;
        display: block;
        width: 1px;
        // set normal header divider height and center it vertically
        height: ${singleHeaderDividerPercent}%;
        top: ${50 - singleHeaderDividerPercent / 2}%;
      }
      &:hover:before,
      &:hover:after {
        background-color: ${theme.columnDividerHover};
      }
    }

    // for taller header cells (those alongsize column group), give a bit more space
    // for the divider, and center it vertically
    .ag-header-cell.ag-header-span-height {
      &:before,
      &:after {
        height: ${doubleHeaderDividerPercent}%;
        top: ${50 - doubleHeaderDividerPercent / 2}%;
      }
    }
  `;
  return { columnDividerColor: hideColumnDivider ? 'transparent' : theme.columnDividerHover, columnDividerCss };
}
